import React, { useRef } from 'react';
import { useModal } from 'components/modals/ModalContext';
import { EventType } from '../SchedulingTypes';
import JobModal from '../../jobs/JobModal';
import { Draggable } from 'services/dragdrop/Draggable';

interface EmployeeEventProps {
    event: EventType,
    currentResource: string | number,
    currentDay: string,
    currentTimeSlot?: string,
    pixelsPerMinute?: number,
    onShowTooltip: (x: number, y: number, event: EventType) => void;
    onHideTooltip: () => void;
};

const EmployeeEvent: React.FC<EmployeeEventProps> = ({
    event, currentResource, currentDay, currentTimeSlot, pixelsPerMinute,
    onShowTooltip, onHideTooltip
}) => {
    const { initializeModal } = useModal();
    const eventEntered = useRef(false);
    const eventPosition = useRef<{ x: number, y: number } | null>(null);

    // Calculate the event start time offset in pixels from the start of the timeslot
    const calculateStartOffset = () => {
        // Return if the needed values are missing
        if (!event.startDateTime || !currentTimeSlot || !pixelsPerMinute) return;

        // Get the minutes of the timeslot and the start time (e.g. '45' of '07:45')
        const startTimeString = event.startDateTime.split('T')[1];
        const startTimeMinutes = Number(startTimeString.split(':')[1]);
        const timeSlotStartMinutes = Number(currentTimeSlot.split(':')[1]);
        
        // Calculate the start offset in minutes and pixels
        const offsetMinutes = startTimeMinutes - timeSlotStartMinutes;
        const offsetPixels = offsetMinutes * pixelsPerMinute;

        return offsetPixels;
    };

    // Calculate the event duration in pixels
    const calculateDurationPixels = () => {
        // Return if the needed values are missing
        if (!event.durationMinutes || !pixelsPerMinute) return;

        // Calculate the duration in pixels
        const durationPixels = event.durationMinutes * pixelsPerMinute;

        return durationPixels;
    };

    // Sets the absolute position of timeline rendered events
    const timeRenderedPosition = (type: 'available' | 'individual_job') => {

        // Calculate the positioning values of the appointment
        const startTimeOffset = calculateStartOffset();
        const durationPixels = calculateDurationPixels();

        let timeRenderedStyle: React.CSSProperties = {};

        if (type === 'available') {
            timeRenderedStyle = {
                position: 'absolute',
                top: '4px',
                left: startTimeOffset ? `calc(${startTimeOffset}px - 4px)` : `4px`,
                width: `calc(${durationPixels}px - 7px)`,
                height: `24px`,
                paddingLeft: `0.25rem`
            };
        }

        if (type === 'individual_job') {

            // Get the stack index to stack the events which are at the same time
            const stackIndex = Number(("stackIndex" in event ? event.stackIndex : 0) ?? 0);
            const eventHeightPixels = 30;

            timeRenderedStyle = {
                position: 'absolute',
                top: `calc(0.05rem + ${stackIndex * eventHeightPixels}px)`,
                left: `calc(${startTimeOffset} - 2px)`,
                width: `${durationPixels}px`,
                height: `1.25rem`,
                paddingTop: `0.25rem`,
                paddingBottom: `0.25rem`,
                marginBottom: `0.25rem`
            };
        }

        return timeRenderedStyle;
    };

    // Handle clicking on the event
    const handleOpenJobModal = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        initializeModal(
            React.cloneElement(<JobModal />), { 
                itemId: Number(event.resourceId), 
                modalSize: 'large' 
            }
        );
    };

    // Show tooltip on mouse enter
    const handleMouseEnter = (e: React.MouseEvent) => {
        eventEntered.current = true;
        eventPosition.current = { x: e.clientX, y: e.clientY };
        onShowTooltip(e.clientX + 12, e.clientY + 12, event);
    };

    // Show tooltip on mouse mouve
    const handleMouseMove = (e: React.MouseEvent) => {
        if (!eventEntered.current && !eventPosition.current) {
            onShowTooltip(e.clientX + 12, e.clientY + 12, event);
        }
    };

    // Hide tooltip on mouse leave
    const handleMouseLeave = () => {
        eventPosition.current = null;
        onHideTooltip();
    };

    return (
        <>
            {event.type === 'availableEmployee' && (
                <Draggable 
                    key={event.key}
                    draggableId={{ type: "availableEmployee", event }}
                    sourceDroppableArea={{ 
                        type: 'availableEmployeesRow',
                        resourceId: currentResource, 
                        day: currentDay,
                        timeSlot: currentTimeSlot
                    }}
                >
                    <div className='event'
                         style={timeRenderedPosition('available')}>
                        <div className='list-event'>
                            <div className='event-content'>
                                {event.employeeName && (
                                    <span>{event.employeeName}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </Draggable>
            )}
            
            {event.type === "projectJobEmployeeEvent" && (
                <Draggable 
                    key={event.key}
                    draggableId={{ type: "projectJobEmployeeEvent", event }}
                    isDragDisabled={(event.status === "incomplete" || event.status === "done")
                        ? { disabled: true, message: "scheduling.scheduling_board.done_appointment_dragging_disabled" }
                        : undefined
                    }
                    sourceDroppableArea={{ 
                        type: 'projectJob',
                        resourceId: currentResource, 
                        day: currentDay,
                        ...currentTimeSlot ? { timeSlot: currentTimeSlot } : {}
                    }}
                >
                    <div className={`event ${event.status}`}
                         onClick={handleOpenJobModal}>
                        <div className='list-event'>
                            <div className='event-content'>
                                {event.assigneeName && (
                                    <span>{event.assigneeName}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </Draggable>
            )}

            {event.type === "individualJobEmployeeEvent" && (
                <Draggable 
                    key={event.key}
                    draggableId={{ type: "individualJobEmployeeEvent", event }}
                    isDragDisabled={(event.status === "incomplete" || event.status === "done")
                        ? { disabled: true, message: "scheduling.scheduling_board.done_appointment_dragging_disabled" }
                        : undefined
                    }
                    sourceDroppableArea={{ 
                        type: 'individualJob',
                        resourceId: currentResource, 
                        day: currentDay,
                        timeSlot: currentTimeSlot
                    }}    
                >
                    <div 
                        className={`event ${event.status}`}
                        onClick={handleOpenJobModal}
                        onMouseEnter={handleMouseEnter}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                        style={timeRenderedPosition('individual_job')}
                    >
                        <div className='list-event'>
                            <div className='event-content'>
                                {event.assigneeName && (
                                    <span>{event.assigneeName}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </Draggable>
            )}
        </>    
    );
};

export default EmployeeEvent;